import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Divider, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Button } from '@/components/ui';
import { usersService, type UserApiModel } from '@/superadmin/services/usersService';
import { rolePermissionsService, type RolePermissionMapping } from '@/superadmin/services/rolePermissionsService';

export interface UserPermissionMappingFormValues {
  user: string;
  role_permission: string;
}

export interface UserPermissionMappingFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: UserPermissionMappingFormValues) => void;
  loading?: boolean;
}

// Using the actual RolePermissionMapping interface from the service

const drawerWidth = 480;

const UserPermissionMappingFormDrawer: React.FC<UserPermissionMappingFormDrawerProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  loading = false 
}) => {
  const [values, setValues] = useState<UserPermissionMappingFormValues>({
    user: '',
    role_permission: '',
  });
  const [users, setUsers] = useState<UserApiModel[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermissionMapping[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setValues({
        user: '',
        role_permission: '',
      });
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    try {
      setDataLoading(true);
      
      // Fetch users and role permission mappings in parallel
      const [usersResponse, rolePermissionsResponse] = await Promise.all([
        usersService.list(),
        rolePermissionsService.getRolePermissionMappings(1, 100) // Fetch all role permission mappings
      ]);
      
      setUsers(usersResponse.results || []);
      setRolePermissions(rolePermissionsResponse.results || []);
    } catch (error) {
      console.warn('Failed to fetch data for user permission mapping:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.user || !values.role_permission) return;
    onSubmit(values);
  };

  const isFormValid = values.user && values.role_permission;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: drawerWidth } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
        <Typography variant="h6">Map User to Permission</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box component="form" onSubmit={handleSubmit} px={2} py={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          User Permission Mapping
        </Typography>
        
        <FormControl fullWidth required>
          <InputLabel id="user-select-label">Select User</InputLabel>
          <Select
            labelId="user-select-label"
            name="user"
            value={values.user}
            onChange={handleChange}
            label="Select User"
            disabled={loading || dataLoading}
          >
            {dataLoading ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading users...</Typography>
                </Box>
              </MenuItem>
            ) : (
              users.map((user) => (
                <MenuItem key={user.uuid} value={user.uuid}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {user.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {user.email} • {user.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl fullWidth required>
          <InputLabel id="role-permission-select-label">Select Role Permission</InputLabel>
          <Select
            labelId="role-permission-select-label"
            name="role_permission"
            value={values.role_permission}
            onChange={handleChange}
            label="Select Role Permission"
            disabled={loading || dataLoading}
          >
            {dataLoading ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading role permissions...</Typography>
                </Box>
              </MenuItem>
            ) : (
              rolePermissions.map((rolePermission) => (
                <MenuItem key={rolePermission.uuid} value={rolePermission.uuid}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {rolePermission.role_name} - {rolePermission.permission_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {rolePermission.permission_description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Box display="flex" gap={1} mt={1}>
          <Button 
            type="submit" 
            variant="primary" 
            disabled={!isFormValid || loading || dataLoading}
            sx={{ flex: 1 }}
          >
            {loading ? 'Creating...' : 'Create Mapping'}
          </Button>
          <Button 
            type="button" 
            variant="outlined" 
            onClick={onClose} 
            disabled={loading || dataLoading}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default UserPermissionMappingFormDrawer;
